import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Track } from "../data/sharedTypes";
import {
    fmCountries,
    getFmCollegesForCountry,
    getFmCountriesForTrack,
    type FmCountry,
} from "../data/foreignMissionsData";
import StepHeader from "../StepHeader";
import FlagIcon from "../components/FlagIcon";

interface Props {
    universityNameKey: string;
    initialTrack?: Track | null;
    onBack: () => void;
    onSelectCountry: (country: FmCountry, track: Track) => void;
    onTrackChange?: (track: Track) => void;
}

/**
 * Foreign Missions — country picker (FM-only step).
 *
 * Track tabs (Scientific / Literary) gate which countries appear:
 *   - Scientific track shows every country that has at least one row.
 *   - Literary track shows only countries with sci-or-literary specialties
 *     (USA, Australia, New Zealand per the 2026/2027 MoHE PDF).
 */
export default function ChooseFmCountry({
    universityNameKey,
    initialTrack,
    onBack,
    onSelectCountry,
    onTrackChange,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const [activeTrack, setActiveTrack] = useState<Track>(initialTrack ?? "scientific");

    const handleTrackChange = (track: Track) => {
        setActiveTrack(track);
        onTrackChange?.(track);
    };

    const visibleCountries = getFmCountriesForTrack(activeTrack);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_fm_choose_country")}
                subtitle={t(universityNameKey)}
            />

            {/* Track tabs */}
            <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
                {(["scientific", "literary"] as Track[]).map((track) => (
                    <button
                        key={track}
                        onClick={() => handleTrackChange(track)}
                        className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                            activeTrack === track
                                ? "bg-white text-theme-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {t(`dream_university_track_${track}`)}
                    </button>
                ))}
            </div>

            {/* Country grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {visibleCountries.map((country) => {
                    const collegeCount = getFmCollegesForCountry(country.key, activeTrack).length;
                    return (
                        <button
                            key={country.key}
                            onClick={() => onSelectCountry(country, activeTrack)}
                            className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200"
                        >
                            {/* Circular flag badge */}
                            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-50 ring-1 ring-gray-200 overflow-hidden">
                                <FlagIcon
                                    iso={country.iso}
                                    title={t(`dream_university_country_${country.key}`)}
                                    className="w-full h-full"
                                />
                            </span>
                            <span className="text-sm font-semibold text-gray-800 text-center leading-tight">
                                {t(`dream_university_country_${country.key}`)}
                            </span>
                            <span className="text-[10px] text-gray-500">
                                {t("dream_university_fm_college_count", { count: collegeCount })}
                            </span>
                        </button>
                    );
                })}
            </div>

            {visibleCountries.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                    {t("dream_university_fm_no_countries_for_track")}
                </p>
            )}

            {/* Hidden helper — keep all 9 country keys referenced so unused-import
                warnings don't trim them. */}
            <span className="hidden">{fmCountries.length}</span>
        </div>
    );
}
